import {ORIGIN} from "./configs";
import { submitUserCommand, userRequest } from "../auth/userApi";
import { commandResultMessage } from "../commands/commandTransport";

const LATEST_CURRENCIES = 15;

const parseText = (text) => {
  const regex = /\#\#ts_(\d+)\#\#/gm;
  return text.replace(regex, (match, timestamp) => convertToDate(timestamp));
};

const convertToDate = (timestamp) => {
  var a = new Date(timestamp * 1000);
  var months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  var year = a.getFullYear();
  var month = months[a.getMonth()];
  var date = a.getDate();
  var hour = a.getHours() > 9 ? a.getHours() : "0" + a.getHours();
  var min = a.getMinutes() > 9 ? a.getMinutes() : "0" + a.getMinutes();
  return `${date} ${month} ${year} ${hour}:${min}`;
};

const getCandleFromDate = (date) => {

  if (!date) {
    return null;
  }

  const pad = (n) => n.toString().padStart(2, '0');

  const formattedDate =
      pad(date.getUTCDate()) + '-' +
      pad(date.getUTCMonth() + 1) + ' ' +
      pad(date.getUTCHours()) + ':' +
      pad(date.getUTCMinutes());

  return formattedDate;
}

const isMobile = () => {
  return /iphone|ipad|ipod|android|blackberry|windows phone/g.test(navigator.userAgent.toLowerCase());
}

const processCommand = async (first, second) => {
  const body = second || first || {};
  const result = await submitUserCommand({ type: "TRADE_COMMAND", payload: { command: body.command || String(body) } });
  if (!result.acknowledgement?.accepted) throw new Error(commandResultMessage(result));
  return result;
}

const parsePrice = (price) => {
  if (price.toString().endsWith("$")) {
    price = price.toString().substring(0, price.length - 1);
  }
  return Number.parseFloat(price);
}

const getTakeProfitOptions = () => {
  return {
    "LONG": [
      "NONE", "HOLD",
      "1%", "1.5%", "2%", "2.5%", "3%",
      "5%", "10%", "20%", "50%", "100%", "200%"
    ],
    "SHORT": [
      "NONE", "HOLD",
      "1%", "1.5%", "2%", "2.5%", "3%",
      "5%", "10%", "20%", "50%", "100%", "200%"
    ]
  };
}

export const isRelativeTPValue = (ts, val) => {
    return getTakeProfitOptions()[ts]?.indexOf(val) !== -1;
}

export const saveOpenedCurrency = (currency) => {

  currency = currency.toString().toUpperCase();
  let openedCurrencies = getUpdatedOpenCurrenciesArr(currency);

  const currIndex = openedCurrencies.indexOf("BTC");
  if (currIndex !== -1) {
    openedCurrencies.splice(currIndex, 1);
  }
  openedCurrencies.unshift(currency);
  if (currency !== "BTC") {
    openedCurrencies.unshift("BTC");
  }

  if (openedCurrencies.length > LATEST_CURRENCIES) {
    openedCurrencies.pop();
  }

  localStorage.setItem("openedCurrencies", JSON.stringify(openedCurrencies));
}

export const removeOpenedCurrency = (currency) => {
  localStorage.setItem("openedCurrencies", JSON.stringify(getUpdatedOpenCurrenciesArr(currency)));
}

const getUpdatedOpenCurrenciesArr = (currency) => {
  if (!currency) {
    return;
  }
  currency = currency.toString().toUpperCase();
  let openedCurrencies = getOpenedCurrencies();
  const currIndex = openedCurrencies.indexOf(currency);
  if (currIndex !== -1) {
    openedCurrencies.splice(currIndex, 1);
  }

  return openedCurrencies;
}

export const getOpenedCurrencies = () => {
  const currStorage = localStorage.getItem("openedCurrencies");
  let openedCurrencies = ["BTC"];
  if (currStorage) {
    try {
      openedCurrencies = JSON.parse(currStorage);
    } catch (e) {
    }
  }
  return openedCurrencies;
}

export const openCurr = (currency) => {
  window.location.assign(`?currency=${currency}`);
}

export const getUserSettings = () => userRequest("/insights/account/settings");

const chartEvaluationRequest = async (url, options = {}) => {
  const { timeoutMs = 15_000, signal, ...requestOptions } = options;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timer = window.setTimeout(abort, timeoutMs);
  try {
    const response = await fetch(`${ORIGIN}${url}`, {
      ...requestOptions,
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(payload.error || "Chart evaluation request failed.");
      error.status = response.status;
      throw error;
    }
    return payload;
  } catch (error) {
    if (controller.signal.aborted) throw new Error(signal?.aborted
      ? "Chart evaluation request cancelled."
      : "Chart evaluation request timed out. Check saved results before submitting again.");
    throw error;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
};

const assessmentQueryString = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([name, value]) => {
    if (value !== "" && value !== null && value !== undefined) params.set(name, String(value));
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
};

export const getAiAssessmentAccess = ({ signal } = {}) => chartEvaluationRequest(
  "/insights/ai-assessments/access", { signal }
);
export const getAiAssessments = (query, { signal } = {}) => chartEvaluationRequest(
  `/insights/ai-assessments${assessmentQueryString(query)}`, { signal }
);
export const getAiAssessment = (id, { signal } = {}) => chartEvaluationRequest(
  `/insights/ai-assessments/${encodeURIComponent(id || "")}`, { signal }
);

export const getChartEvaluationModels = () => chartEvaluationRequest("/insights/chart-evaluation/models");
export const getChartEvaluationProviders = () => chartEvaluationRequest("/insights/chart-evaluation/providers");
export const getChartEvaluationHistory = (symbol) => chartEvaluationRequest(
  `/insights/chart-evaluation/history?symbol=${encodeURIComponent(symbol || "")}`
);
export const getChartEvaluationJob = (jobId) => chartEvaluationRequest(
  `/insights/chart-evaluation/jobs/${encodeURIComponent(jobId)}`
);
export const getChartEvaluationProfile = () => chartEvaluationRequest("/insights/chart-evaluation/profile");
export const saveChartEvaluationProfile = (profile) => chartEvaluationRequest("/insights/chart-evaluation/profile", {
  method: "PUT",
  body: JSON.stringify(profile),
});
export const captureChartEvaluation = (input, { signal } = {}) => chartEvaluationRequest("/insights/chart-evaluation/capture", {
  signal,
  timeoutMs: 30_000,
  method: "POST",
  body: JSON.stringify(input),
});
export const evaluateChart = (input) => chartEvaluationRequest("/insights/chart-evaluation/evaluate", {
  timeoutMs: 120_000,
  method: "POST",
  body: JSON.stringify(input),
});
export const previewChartEvaluation = (input) => chartEvaluationRequest("/insights/chart-evaluation/preview", {
  timeoutMs: 30_000,
  method: "POST",
  body: JSON.stringify(input),
});

export const saveTradedCurrency = (currency, tradeSide) => {
  const tradedCurrencies = JSON.parse(localStorage.getItem("tradedCurrencies")) || {};
  tradedCurrencies[currency] = {};
  tradedCurrencies[currency].tradeSide = tradeSide;
  localStorage.setItem("tradedCurrencies", JSON.stringify(tradedCurrencies));
}

export const getSortedSections = (sections, settings) => {

  const sortedSections = sections.sort((section1, section2) => {
    const ord1 = getSectOrder(settings, section1.title);
    const ord2 = getSectOrder(settings, section2.title);

    if (ord1 === ord2) {
      return 0;
    } else if (ord1 > ord2) {
      return 1;
    } else {
      return -1;
    }
  });

  return sortedSections;
}

const getSectOrder = (settings, key) => {
  return settings?.sectionSettings?.[key]?.order || 1000;
}

export {
  parseText,
  convertToDate,
  getCandleFromDate,
  processCommand,
  parsePrice,
  getTakeProfitOptions,
  isMobile
};
